import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Flex, Text } from 'rebass/styled-components';

import { FilePreviewWrapper } from './styles';

import {
  MainContent,
  Tree,
  EmptyState,
  Card,
  CardHeader,
  CardBody,
  SlidePanel,
  SearchableList,
  ListBody,
  ListSearch,
  List,
  FormInput,
  Loader,
} from 'ui/patterns';
import { Button, FormLabel, Dropzone } from 'ui/components';

import {
  useCreateBlobDirectory,
  useDeleteBlobFile,
  useDocumentTitle,
  useGetAccountsListForImportFiles,
  useGetBlobFiles,
  useImportBlobFiles,
  useUploadFile,
} from 'hooks';

import * as Icon from 'assets/icons';
import NoDataImage from 'assets/images/NoDataImage';

import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { apiBaseUrl } from 'utils/urls';
import { isValidFile, getEncodedFilePath, accept } from 'utils/fileUtils';

import { BlobType } from 'types/procedureBuilder';
import { ImportFileAccountList } from 'types/accounts';
import { ErrorModal } from 'types/modalTypes';
import { FileBrowserType } from './fileBrowserTypes';

const toBase64 = (file: File): any => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

export default function FileBrowser({
  showPanel,
  togglePanel: toggle,
  account,
}: FileBrowserType): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [openImportFile, setOpenImportFile] = useState<BlobType>();
  const [selectedFile, setSelectedFile] = useState<BlobType>();
  const [folderName, setFolderName] = useState<string>('');
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [showAddFolder, setShowAddFolder] = useState<BlobType>();
  const [selectedFiles, setSelectedFiles] = useState<Array<BlobType>>();
  const [showFileUpload, setShowFileUpload] = useState<BlobType>();
  const [searchAccounts, setSearchAccounts] = useState<string>('');
  const [selectedAccount, setSelectedAccount] =
    useState<ImportFileAccountList>();
  const [showFeedback, setShowFeedback] = useState(false);

  useDocumentTitle('Procedure Builder', true);

  const history = useHistory<{ ID: number; account?: string }>();
  const tbodyRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  const params = useParams<{
    accountId: string;
    tabId?: string;
  }>();

  const accountId = parseInt(params.accountId);

  const {
    mutate: createBlobDirectory,
    isLoading: isCreateBlobDirectoryLoading,
  } = useCreateBlobDirectory();

  const { mutate: uploadFile, isLoading: isuploadFileLoading } =
    useUploadFile();

  const { mutate: deleteFile, isLoading: isDeleteFileLoading } =
    useDeleteBlobFile();

  const { data: blobFiles, isLoading: isBlobFilesLoading } = useGetBlobFiles({
    accountId,
    uniqueId: account,
  });

  const { mutate: importBlobFiles, isLoading: isImportLoading } =
    useImportBlobFiles();

  const {
    data: accountsList,
    isLoading: isAccountsListForImportFilesLoading,
    fetchNextPage,
    hasNextPage,
  } = useGetAccountsListForImportFiles(accountId, searchAccounts);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  useEffect(() => {
    if (accountId === undefined || account === undefined) {
      history.push('/home');
    }
  }, [accountId, account, history]);

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  function toggleOpenImportFile(folder?: BlobType) {
    setOpenImportFile(folder);
  }

  const toggleAddFolder = (folder?: BlobType) => {
    setShowAddFolder(folder);
    setShowFeedback(false);
  };

  const toggleFileUpload = (folder?: BlobType) => {
    setShowFileUpload(folder);
  };

  function updatedSelectedFiles(files: Array<BlobType>) {
    setSelectedFiles((selectedFiles?: Array<BlobType>) => {
      if (selectedFiles) {
        const newselectedFiles = selectedFiles.filter(selectedFile => {
          const isExists = files.find(file => selectedFile.Path === file.Path);
          return !isExists;
        });
        if (newselectedFiles.length === selectedFiles?.length) {
          return [...selectedFiles, ...files];
        } else return newselectedFiles;
      } else {
        return [...files];
      }
    });
  }

  const allAccountsList = useMemo(() => {
    return accountsList?.pages?.map((dataItem: any) => dataItem.Data).flat();
  }, [accountsList]);

  const {
    data: selectedAccountBlobFiles,
    isLoading: isSelectedAccountBlobFilesLoading,
  } = useGetBlobFiles({
    accountId: selectedAccount ? selectedAccount.AccountId : 0,
    uniqueId: selectedAccount
      ? `${selectedAccount?.CID}${'-'}${selectedAccount?.SID}${'-'}${
          selectedAccount?.PID
        }`
      : undefined,
  });

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  function onFileChange(file?: File): void {
    if (!file) {
      setSelectedFile(undefined);
      setFileToUpload(file);
      return;
    }
    const regex = /^[a-zA-Z0-9\s_\\.\-\(\)~'@$;,!=:\[\]]+$/;
    if (!regex.test(file.name)) {
      showErrorToast({
        message: 'Please choose a valid file to upload',
      });
      return;
    } else {
      const fileModules = file.name.split('.');
      const fileExtension = fileModules[fileModules.length - 1];
      if (isValidFile(fileExtension)) {
        setSelectedFile(undefined);
        setFileToUpload(file);
      } else {
        showErrorToast({
          message: 'Please choose a valid file to upload',
        });
      }
    }
  }

  const importActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          toggleOpenImportFile(undefined);
          setOpenImportFile(undefined);
          setSelectedFiles([]);
          setSelectedAccount(undefined);
          setSearchAccounts('');
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={() => {
          if (selectedFiles && selectedFiles.length > 0 && openImportFile) {
            importBlobFiles(
              {
                params: {
                  DestinationPath: openImportFile.Path,
                  BlobDirectoryList: [...selectedFiles],
                },
                accountId: accountId,
              },
              {
                onSuccess: () => {
                  setOpenImportFile(undefined);
                  setSelectedFiles([]);
                  setSelectedAccount(undefined);
                  toggleOpenImportFile(undefined);
                  setSearchAccounts('');
                },
              },
            );
          }
        }}
        variant="primary"
      >
        Import
      </Button>
    </>
  );

  const addFolderActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setShowAddFolder(undefined);
          setFolderName('');
          setShowFeedback(false);
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={(() => {
          if (showFeedback) {
            const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
              blobFile =>
                blobFile.Type === 'folder' && blobFile.Name === folderName,
            );
            if (folderNameAlreadyExists) {
              return true;
            }
          }
          return false;
        })()}
        onClick={() => {
          if (showAddFolder && folderName) {
            const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
              blobFile =>
                blobFile.Type === 'folder' && blobFile.Name === folderName,
            );
            if (folderNameAlreadyExists) {
              setShowFeedback(true);
              return;
            }
            createBlobDirectory(
              {
                params: {
                  Title: `${showAddFolder?.Path}${folderName}/do-not-delete-this-file.txt`,
                  Content: `${showAddFolder?.Path}${folderName}`,
                  Container: `root`,
                  streamContent: '',
                },
                accountId: accountId,
              },
              {
                onSuccess: () => {
                  showSuccessToast({
                    message: 'Folder created successfully',
                  });
                  toggleAddFolder(undefined);
                  setFolderName('');
                },
                onError: (error: ErrorModal) => {
                  showErrorToast({
                    message: error.Errors[0].Message,
                  });
                },
              },
            );
          }
        }}
        variant="primary"
      >
        Add folder
      </Button>
    </>
  );

  const uploadFilesActions = () => (
    <>
      <Button variant="secondary" onClick={() => setShowFileUpload(undefined)}>
        Cancel
      </Button>
      <Button
        onClick={async () => {
          if (fileToUpload) {
            const base64 = await toBase64(fileToUpload);
            uploadFile(
              {
                accountId,
                params: {
                  Title: `${showFileUpload?.Path}${fileToUpload.name}`,
                  Container: `root`,
                  Content: `C:\\fakepath\\${showFileUpload?.Path}`,
                  streamContent: base64,
                },
              },
              {
                onSuccess: () => {
                  showSuccessToast({
                    message: 'File uploaded successfully',
                  });
                  toggleFileUpload(undefined);
                  setFolderName('');
                  setFileToUpload(undefined);
                },
                onError: (error: ErrorModal) => {
                  showErrorToast({
                    message: error.Errors[0].Message,
                  });
                },
              },
            );
          }
        }}
        variant="primary"
      >
        Upload
      </Button>
    </>
  );

  const panelActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel && togglePanel();
          // reset();
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={
          !selectedFile || selectedFile?.Type === 'folder' ? true : false
        }
        onClick={() => {
          if (selectedFile)
            window.open(
              `${apiBaseUrl}${'accounts/procedures/get-external-file?filepath=root/'}${
                selectedFile?.Path
              }`,
              '_blank',
            );
        }}
        variant="primary"
      >
        {'Download File'}
      </Button>
    </>
  );
  const isLoading =
    isBlobFilesLoading ||
    isImportLoading ||
    isuploadFileLoading ||
    isSelectedAccountBlobFilesLoading ||
    isCreateBlobDirectoryLoading ||
    isDeleteFileLoading;

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        togglePanel && togglePanel();
      }}
      size="lg"
      title="File Browser"
      actions={panelActions()}
    >
      <MainContent flexDirection="column">
        <Flex height="600px" flex="1 1 auto" p={3}>
          <Card
            width="350px"
            flexDirection="column"
            style={{ display: 'flex' }}
          >
            <CardHeader
              p={3}
              bg="var(--color-neutral-contrast-02)"
              alignItems="center"
              style={{ display: 'flex' }}
            >
              <Text
                as="h4"
                fontWeight="medium"
                lineHeight={1}
                color="text.body"
              >
                Files
              </Text>
            </CardHeader>
            <CardBody p={2} flex="1 0 auto" overflowY="auto" height="1px">
              <Tree
                account={account}
                setSelectedFile={setSelectedFile}
                files={
                  blobFiles && blobFiles.Data ? blobFiles.Data[0] : undefined
                }
                selectedFile={selectedFile}
                toggleAddFolder={toggleAddFolder}
                toggleFileUpload={toggleFileUpload}
                toggleOpenImportFile={toggleOpenImportFile}
                deleteFile={(file: BlobType) => {
                  deleteFile(
                    {
                      accountId: accountId,
                      params: {
                        Container: 'root',
                        Title:
                          file.Type === 'folder'
                            ? `${file?.Path}do-not-delete-this-file.txt`
                            : file.Path,
                      },
                    },
                    {
                      onSuccess: () => {
                        showSuccessToast({
                          message:
                            file.Type === 'folder'
                              ? 'Folder deleted successfully'
                              : 'File deleted successfully',
                        });
                        setSelectedFile(undefined);
                      },
                    },
                  );
                }}
              />
            </CardBody>
          </Card>
          <FilePreviewWrapper>
            <EmptyState>
              {!selectedFile ? (
                <NoDataImage feedback="Please select a file to preview" />
              ) : null}
              {selectedFile && selectedFile.Type === 'image' ? (
                <img src={selectedFile.Uri} />
              ) : null}
              {selectedFile &&
              selectedFile.Type !== 'image' &&
              selectedFile.Type !== 'folder' ? (
                <iframe
                  src={`${apiBaseUrl}${'accounts/procedures/get-external-file?filePath='}${getEncodedFilePath(
                    selectedFile.Uri,
                  )}`}
                  style={{ width: '100%', height: '100%' }}
                ></iframe>
              ) : null}
            </EmptyState>
          </FilePreviewWrapper>
        </Flex>

        {/* Import Files */}
        <SlidePanel
          title="Import Files"
          open={!!openImportFile}
          onClose={() => {
            toggleOpenImportFile(undefined);
            setOpenImportFile(undefined);
            setSelectedFiles([]);
            setSelectedAccount(undefined);
            setSearchAccounts('');
          }}
          size="lg"
          actions={importActions()}
        >
          <Flex height="100%">
            <Flex flexDirection="column">
              <FormLabel>Accounts</FormLabel>
              <SearchableList width="400px" flex={1}>
                <ListSearch>
                  <input
                    value={searchAccounts}
                    onChange={e => {
                      setSearchAccounts(e.currentTarget.value);
                    }}
                    type="search"
                    placeholder="Search account name / CID"
                  />
                  <Icon.Search />
                </ListSearch>
                {allAccountsList && allAccountsList.length > 0 ? (
                  <ListBody ref={tbodyRef}>
                    <List>
                      {allAccountsList.map(
                        (account?: ImportFileAccountList) => (
                          <li
                            key={account?.AccountNameAndNumber}
                            onClick={() => {
                              if (account) {
                                setSelectedAccount({ ...account });
                                setSelectedFiles([]);
                              }
                            }}
                            className={
                              selectedAccount &&
                              account?.AccountId === selectedAccount.AccountId
                                ? 'active'
                                : ''
                            }
                          >
                            {account?.AccountNameAndNumber}
                          </li>
                        ),
                      )}
                    </List>
                  </ListBody>
                ) : (
                  <>
                    {isAccountsListForImportFilesLoading ? (
                      <Card
                        flex="1"
                        position="relative"
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        <Loader />
                      </Card>
                    ) : (
                      <Card
                        flex="1"
                        position="relative"
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        <EmptyState>
                          <NoDataImage feedback="No data available" />
                        </EmptyState>
                      </Card>
                    )}
                  </>
                )}
              </SearchableList>
            </Flex>

            <Flex ml={3} flex="1" flexDirection="column">
              <FormLabel>Files</FormLabel>
              <Card
                flex="1 0 auto"
                p={3}
                height="1px"
                overflowX="auto"
                bg="var(--color-neutral-contrast-02)"
              >
                <Tree
                  selectMode={true}
                  updatedSelectedFiles={updatedSelectedFiles}
                  selectedFiles={selectedFiles}
                  files={
                    selectedAccountBlobFiles && selectedAccountBlobFiles.Data
                      ? selectedAccountBlobFiles.Data[0]
                      : undefined
                  }
                  toggleOpenImportFile={toggleOpenImportFile}
                  account={account}
                />
              </Card>
            </Flex>
          </Flex>
        </SlidePanel>

        {/* Add Folder */}
        <SlidePanel
          title="Add New Folder"
          open={!!showAddFolder}
          onClose={() => {
            setFolderName('');
            setShowAddFolder(undefined);
            setShowFeedback(false);
          }}
          size="sm"
          actions={addFolderActions()}
        >
          <FormInput
            value={folderName}
            onTextChange={setFolderName}
            label="Enter folder name"
            feedback={(() => {
              if (showFeedback) {
                const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
                  blobFile =>
                    blobFile.Type === 'folder' && blobFile.Name === folderName,
                );
                if (folderNameAlreadyExists) {
                  return 'Folder name is already exist, Please try with another name';
                }
              }
              return '';
            })()}
          />
        </SlidePanel>

        {/* Upload Files */}
        <SlidePanel
          title="Upload Files"
          open={!!showFileUpload}
          onClose={() => setShowFileUpload(undefined)}
          size="md"
          actions={uploadFilesActions()}
        >
          <Dropzone
            accept={accept}
            onChange={(file?: File) => {
              onFileChange(file);
            }}
            value={fileToUpload?.name}
            flex={1}
            alignSelf="center"
            height="100%"
            info="Special characters not allowed in fileName(+, #, {, }, `, %, &, /, *, ^)"
          />
        </SlidePanel>
        {isLoading && <Loader />}
      </MainContent>
    </SlidePanel>
  );
}
